"use client";

import { Zap, Crown, AlertTriangle, Info } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { useGetSubscriptionDetailsQuery, useLazyCancelSubscriptionQuery } from "@/services/paymentApi";
import { Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSelector } from "react-redux";
import { RootState } from "@/store";

export default function CurrentPlanCard({ onRefetchNeeded, isExternalLoading = false }: { onRefetchNeeded?: () => void, isExternalLoading?: boolean }) {
  const user = useSelector((s: RootState) => s.auth.user);
  const { data, refetch, isLoading } = useGetSubscriptionDetailsQuery(user?.uid, { refetchOnMountOrArgChange: true });
  const subData = data?.subscription;
  const tokenData = data?.tokens;

  const [cancelSub, { isFetching: isCanceling }] = useLazyCancelSubscriptionQuery();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [cancelSuccessMsg, setCancelSuccessMsg] = useState("");

  const planName = subData?.type || "Free Tier";
  const status = subData?.status || "inactive";
  const cancellationScheduled = subData?.cancellation_scheduled === true;

  const maxCredits = Math.round((tokenData?.available_credits ?? 0) * 10) / 10;
  const creditsUsed = tokenData?.total_credits_used ?? 0;
  const remainingCredits = Math.round(Math.max(0, maxCredits - creditsUsed) * 10) / 10;

  const percentage = maxCredits > 0 ? Math.min(100, Math.max(0, Math.round((remainingCredits / maxCredits) * 100))) : 0;

  const formattedEndDate = subData?.end_date
    ? new Date(subData.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  if (isLoading || isExternalLoading) {
    return (
      <div className="flex h-[180px] w-full flex-col justify-center items-center rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-4 shadow-[0_20px_70px_rgba(0,0,0,0.28)]">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  const handleCancelClick = async () => {
    try {
      const resp = await cancelSub().unwrap();
      if (resp?.success) {
        setCancelSuccessMsg(resp.message || "Subscription successfully canceled.");
        refetch();
        setTimeout(() => {
          setIsDialogOpen(false);
          setCancelSuccessMsg("");
        }, 3500);
      }
    } catch (err) {
      console.error("Failed to cancel subscription", err);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col justify-between rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-4 shadow-[0_20px_70px_rgba(0,0,0,0.28)]">
      <div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(172,39,79,0.12)] border border-[#8d4b61]">
              <Crown className="h-5 w-5 text-[#f2a6bc]" />
            </div>
            <div>
              <p className="text-xs font-medium text-zinc-400">Current Plan</p>
              <div className="flex items-center gap-2 mt-0.5">
                <h2 className="text-lg font-semibold text-white">{planName}</h2>
                <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${status === 'active'
                    ? "bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.2)] text-emerald-400"
                    : "bg-white/5 border border-white/10 text-zinc-400"
                  }`}>
                  {status}
                </span>
              </div>
            </div>
          </div>

          <Dialog.Root open={isDialogOpen} onOpenChange={(open) => {
            if (!isCanceling && !cancellationScheduled) setIsDialogOpen(open);
          }}>
            <Dialog.Trigger asChild>
              <button disabled={planName === 'Free Tier' || planName === 'Free' || cancellationScheduled} className="disabled:opacity-50 disabled:cursor-not-allowed inline-flex cursor-pointer items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-[11px] font-medium text-red-400 transition hover:bg-red-500/20 hover:text-red-300">
                {cancellationScheduled ? "Cancellation Scheduled" : "Cancel Subscription"}
              </button>
            </Dialog.Trigger>

            {/* Simple confirmation dialog for cancellation */}
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
              <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-md translate-x-[-50%] translate-y-[-50%] p-6 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-[28px] border border-white/10 bg-[#0c0c0c] shadow-[0_24px_100px_rgba(0,0,0,0.8)]">
                <div className="flex flex-col items-center text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 border border-red-500/20 mb-4">
                    <AlertTriangle className="h-6 w-6 text-red-500" />
                  </div>
                  <Dialog.Title className="text-lg font-semibold text-white">Cancel Subscription</Dialog.Title>
                  <Dialog.Description className="mt-2 text-sm text-zinc-400">
                    Are you sure you want to cancel your {planName} subscription? You will lose access to premium features at the end of your current billing cycle.
                  </Dialog.Description>

                  {cancelSuccessMsg ? (
                    <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-400 text-sm font-medium">
                      {cancelSuccessMsg}
                    </div>
                  ) : (
                    <div className="mt-6 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
                      <Dialog.Close disabled={isCanceling} className="flex-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed rounded-xl border border-white/10 bg-white/5 py-2.5 text-sm font-medium text-zinc-300 transition hover:bg-white/10 hover:text-white">
                        Keep Plan
                      </Dialog.Close>
                      <button
                        onClick={handleCancelClick}
                        disabled={isCanceling}
                        className="flex-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center rounded-xl bg-red-600 py-2.5 text-sm font-medium text-white transition hover:bg-red-700"
                      >
                        {isCanceling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes, Cancel"}
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>

      <div className="mt-4 sm:mt-5">

        {cancellationScheduled && (
          <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-amber-500/90 text-xs leading-relaxed">
            Your subscription has been canceled. You will safely retain full access to all included benefits until the end of your current billing cycle.
          </div>
        )}

        <div className="flex items-end justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-amber-400 fill-amber-400/20" />
            <p className="text-xs font-medium text-zinc-300 flex items-center gap-1">
              Remaining Credits: <span className="font-semibold text-white">{remainingCredits}</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <p className="text-xs text-zinc-500 flex items-center gap-1">
              Maximum Credits: {maxCredits}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer outline-none">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-[#1a1a1a] text-zinc-300 border border-white/10 text-[11px] p-2.5 max-w-[200px] shadow-xl">
                  <p>Maximum credits are the combination of your current cycle&apos;s credits and any carry-forwarded credits.</p>
                </TooltipContent>
              </Tooltip>
            </p>
          </div>
        </div>

        {/* Progress Bar Container */}
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/5 border border-white/5">
          {/* Progress fill */}
          <div
            className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#ac274f] to-[#eb638b] transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(235,99,139,0.5)]"
            style={{ width: `${percentage}%` }}
          />
        </div>

        <p className="mt-1.5 text-[11px] text-zinc-500 text-right">
          {formattedEndDate ? `Subscription renews on ${formattedEndDate}` : "Renews monthly"}
        </p>
      </div>
    </div>
  );
}
